import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, X, Loader2, Wrench } from "lucide-react";
import { useCreateToolLicense, useUpdateToolLicense, useDeleteToolLicense } from "@/hooks/useAIInventory";
import { licenseTypeLabels } from "./shared";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import ImportFromProjectsDialog, { Candidate } from "./ImportFromProjectsDialog";
import { useQueryClient } from "@tanstack/react-query";

interface ToolsTabProps {
  tools: any[];
  aiToolCatalog: { name: string; vendor: string; category: string }[];
  orgId: string | null;
  isSuperAdmin: boolean;
  selectedOrg: string;
  profileOrgId?: string;
  onDeleteConfirm: (item: { type: string; id: string; name: string }) => void;
}

export default function ToolsTab({ tools, aiToolCatalog, orgId, isSuperAdmin, selectedOrg, profileOrgId, onDeleteConfirm }: ToolsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [projectsModalTool, setProjectsModalTool] = useState<any | null>(null);
  
  // Filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Form state
  const [isCustom, setIsCustom] = useState(false);
  const [newTool, setNewTool] = useState({ tool_name: "", category: "", vendor: "", license_type: "per_user", licenses_contracted: 0, cost_per_license: 0 });

  const createTool = useCreateToolLicense();
  const updateTool = useUpdateToolLicense();
  const qc = useQueryClient();

  const categories = useMemo(() => [...new Set(tools.map((t: any) => t.category).filter(Boolean))], [tools]);
  const vendors = useMemo(() => [...new Set(tools.map((t: any) => t.vendor).filter(Boolean))], [tools]);

  const filtered = useMemo(() => {
    return tools.filter((tool: any) => {
      const matchesSearch = (tool.tool_name || '').toLowerCase().includes(search.toLowerCase()) ||
                           (tool.vendor || '').toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === "all" || tool.category === categoryFilter;
      const matchesVendor = vendorFilter === "all" || tool.vendor === vendorFilter;
      const matchesStatus = statusFilter === "all" || tool.status === statusFilter;
      return matchesSearch && matchesCategory && matchesVendor && matchesStatus;
    });
  }, [tools, search, categoryFilter, vendorFilter, statusFilter]);

  const totalCost = tools.reduce((sum: number, t: any) => sum + (t.total_cost || 0), 0);
  const totalLicenses = tools.reduce((sum: number, t: any) => sum + (t.licenses_contracted || 0), 0);
  const usedLicenses = tools.reduce((sum: number, t: any) => sum + (t.licenses_used || 0), 0);

  const getOrgIdForInsert = () => {
    if (isSuperAdmin) {
      if (selectedOrg === 'all') { toast.error('Selecciona una organización específica para agregar datos'); return null; }
      return selectedOrg;
    }
    return profileOrgId;
  };

  const handleAdd = () => {
    const oid = getOrgIdForInsert();
    if (!oid) return;
    createTool.mutate({
      organization_id: oid,
      ...newTool,
      licenses_used: 0,
      total_cost: newTool.licenses_contracted * newTool.cost_per_license,
      status: 'active',
    }, {
      onSuccess: () => {
        setNewTool({ tool_name: "", category: "", vendor: "", license_type: "per_user", licenses_contracted: 0, cost_per_license: 0 });
        setIsCustom(false);
        setIsDialogOpen(false);
      }
    });
  };

  const handleEdit = () => {
    if (!editing) return;
    updateTool.mutate({
      id: editing.id,
      tool_name: editing.tool_name,
      category: editing.category,
      vendor: editing.vendor,
      license_type: editing.license_type,
      licenses_contracted: editing.licenses_contracted,
      licenses_used: editing.licenses_used,
      cost_per_license: editing.cost_per_license,
      total_cost: editing.licenses_contracted * editing.cost_per_license,
      status: editing.status,
    }, { onSuccess: () => setEditing(null) });
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle>Herramientas de IA Externas</CardTitle>
            <div className="flex gap-2">
              <ImportFromProjectsDialog
                type="tools"
                orgId={orgId}
                existingNames={tools.map((t: any) => t.tool_name).filter(Boolean)}
                aiToolCatalog={aiToolCatalog}
                onImport={async (items: Candidate[]) => {
                  const oid = getOrgIdForInsert();
                  if (!oid) throw new Error("Selecciona organización");
                  const rows = items.map((c) => ({
                    organization_id: oid,
                    tool_name: c.name,
                    vendor: c.meta?.vendor || null,
                    category: c.meta?.category || null,
                    license_type: "per_user" as const,
                    licenses_contracted: 0,
                    licenses_used: 0,
                    cost_per_license: 0,
                    total_cost: 0,
                    status: "active" as const,
                    projects_using: c.sourceProjects,
                  }));
                  const { error } = await supabase.from("ai_tool_licenses").insert(rows as any);
                  if (error) throw error;
                  qc.invalidateQueries({ queryKey: ["ai_tool_licenses"] });
                }}
              />
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (open) setIsCustom(false); }}>
                <DialogTrigger asChild>
                  <Button><Plus className="mr-2 h-4 w-4" />Agregar Herramienta</Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nueva Herramienta de IA</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Checkbox id="custom-tool" checked={isCustom} onCheckedChange={(v) => {
                      setIsCustom(!!v);
                      if (v) setNewTool({ ...newTool, tool_name: "", vendor: "", category: "" });
                    }} />
                    <Label htmlFor="custom-tool" className="text-sm cursor-pointer">Herramienta personalizada (no está en el catálogo)</Label>
                  </div>

                  {isCustom ? (
                    <>
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input value={newTool.tool_name} onChange={(e) => setNewTool({...newTool, tool_name: e.target.value})} placeholder="Nombre de la herramienta" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Vendor</Label>
                          <Input value={newTool.vendor} onChange={(e) => setNewTool({...newTool, vendor: e.target.value})} placeholder="Proveedor" />
                        </div>
                        <div className="space-y-2">
                          <Label>Categoría</Label>
                          <Input value={newTool.category} onChange={(e) => setNewTool({...newTool, category: e.target.value})} placeholder="Categoría" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label>Herramienta</Label>
                      <Select value={newTool.tool_name} onValueChange={(v) => {
                        const selected = aiToolCatalog.find(t => t.name === v);
                        if (selected) setNewTool({...newTool, tool_name: selected.name, vendor: selected.vendor, category: selected.category});
                      }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar herramienta" /></SelectTrigger>
                        <SelectContent>
                          {aiToolCatalog.map(t => (
                            <SelectItem key={t.name} value={t.name}>{t.name} — {t.vendor}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {newTool.tool_name && (
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div className="space-y-1">
                            <Label className="text-muted-foreground text-xs">Vendor</Label>
                            <p className="text-sm font-medium">{newTool.vendor}</p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-muted-foreground text-xs">Categoría</Label>
                            <p className="text-sm font-medium">{newTool.category}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Tipo de Licencia</Label>
                    <Select value={newTool.license_type} onValueChange={(v) => setNewTool({...newTool, license_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_user">Por usuario</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                        <SelectItem value="freemium">Freemium</SelectItem>
                        <SelectItem value="open_source">Open Source</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Licencias Contratadas</Label>
                      <Input type="number" value={newTool.licenses_contracted} onChange={(e) => setNewTool({...newTool, licenses_contracted: parseInt(e.target.value) || 0})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Costo por Licencia (USD)</Label>
                      <Input type="number" value={newTool.cost_per_license} onChange={(e) => setNewTool({...newTool, cost_per_license: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>
                  <Button onClick={handleAdd} className="w-full" disabled={!newTool.tool_name || createTool.isPending}>
                    {createTool.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Agregar Herramienta
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar herramienta..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
              {search && <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => setSearch("")}><X className="h-3 w-3" /></Button>}
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((cat: string) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {vendors.map((v: string) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[120px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="inactive">Inactivo</SelectItem>
              </SelectContent>
            </Select>
            {(search || categoryFilter !== "all" || vendorFilter !== "all" || statusFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setCategoryFilter("all"); setVendorFilter("all"); setStatusFilter("all"); }}>Limpiar filtros</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Herramienta</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Tipo Licencia</TableHead>
                <TableHead className="text-center">Licencias</TableHead>
                <TableHead className="text-right">Costo/Lic</TableHead>
                <TableHead className="text-right">Costo Total</TableHead>
                <TableHead>Proyectos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">{tools.length === 0 ? 'No hay herramientas registradas' : 'No se encontraron herramientas con los filtros aplicados'}</TableCell></TableRow>
              ) : filtered.map((tool: any) => (
                <TableRow key={tool.id}>
                  <TableCell>
                    <div><div className="font-medium">{tool.tool_name}</div><div className="text-xs text-muted-foreground">{tool.category}</div></div>
                  </TableCell>
                  <TableCell>{tool.vendor}</TableCell>
                  <TableCell><Badge variant="outline">{licenseTypeLabels[tool.license_type] || tool.license_type}</Badge></TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className="font-medium">{tool.licenses_used || 0}</span><span className="text-muted-foreground">/</span><span>{tool.licenses_contracted || 0}</span>
                    </div>
                    <Progress value={tool.licenses_contracted > 0 ? ((tool.licenses_used || 0) / tool.licenses_contracted) * 100 : 0} className="h-1 mt-1" />
                  </TableCell>
                  <TableCell className="text-right">${(tool.cost_per_license || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-medium">${(tool.total_cost || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="text-primary underline p-0 h-auto" onClick={() => setProjectsModalTool(tool)}>Ver proyectos</Button>
                  </TableCell>
                  <TableCell>
                    <Badge className={tool.status === "active" ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"}>
                      {tool.status === "active" ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => setEditing({ ...tool })}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => onDeleteConfirm({ type: 'tool', id: tool.id, name: tool.tool_name })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>{filtered.length} de {tools.length} herramientas</span>
            <div className="flex items-center gap-4">
              <span>Costo total: <strong className="text-foreground">${totalCost.toLocaleString()}/mes</strong></span>
              <span>Uso: <strong className="text-foreground">{usedLicenses}/{totalLicenses} licencias</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Tool Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Herramienta</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Herramienta</Label>
                <Select value={editing.tool_name} onValueChange={(v) => {
                  const selected = aiToolCatalog.find(t => t.name === v);
                  if (selected) setEditing({...editing, tool_name: selected.name, vendor: selected.vendor, category: selected.category});
                }}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar herramienta" /></SelectTrigger>
                  <SelectContent>
                    {aiToolCatalog.map(t => <SelectItem key={t.name} value={t.name}>{t.name} — {t.vendor}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label className="text-muted-foreground text-xs">Vendor</Label><p className="text-sm font-medium">{editing.vendor}</p></div>
                <div className="space-y-1"><Label className="text-muted-foreground text-xs">Categoría</Label><p className="text-sm font-medium">{editing.category}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Licencia</Label>
                  <Select value={editing.license_type || 'per_user'} onValueChange={(v) => setEditing({...editing, license_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_user">Por usuario</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                      <SelectItem value="freemium">Freemium</SelectItem>
                      <SelectItem value="open_source">Open Source</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({...editing, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Licencias Contratadas</Label><Input type="number" value={editing.licenses_contracted} onChange={(e) => setEditing({...editing, licenses_contracted: parseInt(e.target.value) || 0})} /></div>
                <div className="space-y-2"><Label>Licencias Usadas</Label><Input type="number" value={editing.licenses_used} onChange={(e) => setEditing({...editing, licenses_used: parseInt(e.target.value) || 0})} /></div>
              </div>
              <div className="space-y-2"><Label>Costo por Licencia (USD)</Label><Input type="number" value={editing.cost_per_license} onChange={(e) => setEditing({...editing, cost_per_license: parseFloat(e.target.value) || 0})} /></div>
              <Button onClick={handleEdit} className="w-full" disabled={updateTool.isPending}>
                {updateTool.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Linked Projects Modal */}
      <LinkedProjectsModal toolName={projectsModalTool?.tool_name} orgId={orgId} open={!!projectsModalTool} onClose={() => setProjectsModalTool(null)} />
    </>
  );
}

function LinkedProjectsModal({ toolName, orgId, open, onClose }: { toolName: string | undefined; orgId: string | null; open: boolean; onClose: () => void }) {
  const scopedOrg = orgId && orgId !== 'all' ? orgId : null;
  const { data: linkedProjects = [], isLoading } = useQuery({
    queryKey: ['linked-projects-by-tool', toolName, scopedOrg],
    queryFn: async () => {
      if (!toolName) return [];
      let q = supabase
        .from('project_tools')
        .select('project_id, participant_projects!inner(id, name, status, user_id, profiles!participant_projects_user_id_fkey!inner(full_name, organization_id))')
        .eq('tool_name', toolName);
      if (scopedOrg) {
        q = q.eq('participant_projects.profiles.organization_id', scopedOrg);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map((row: any) => ({
        projectId: row.participant_projects?.id,
        name: row.participant_projects?.name || 'Sin nombre',
        status: row.participant_projects?.status || '—',
        userName: row.participant_projects?.profiles?.full_name || '—',
      }));
    },
    enabled: open && !!toolName,
  });


  const labels: Record<string, string> = { ideation: 'Ideación', in_progress: 'En progreso', submitted: 'Enviado', completed: 'Completado' };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader><DialogTitle>Proyectos usando {toolName}</DialogTitle></DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : linkedProjects.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">Esta herramienta no está siendo utilizada en ningún proyecto de esta organización.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead className="w-10">#</TableHead><TableHead>Proyecto</TableHead><TableHead>Estado</TableHead><TableHead>Participante</TableHead></TableRow></TableHeader>
              <TableBody>
                {linkedProjects.map((p: any, i: number) => (
                  <TableRow key={p.projectId || i}>
                    <TableCell className="font-medium text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell><Badge variant="secondary">{labels[p.status] || p.status}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{p.userName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
}
